import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LanguageIcon from '@mui/icons-material/Language';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import type { SxProps, Theme } from '@mui/material/styles';
import { useColorMode } from '../../hooks/useColorMode';
import { COLORS } from '../../theme/colors';
import type { NavLink } from '../../types/content';

const NAV_LINKS = [
  { key: 'nav.inicio', path: '/' },
  { key: 'nav.queGanas', path: '/que-ganas' },
  { key: 'nav.comoFunciona', path: '/como-funciona' },
  { key: 'nav.planes', path: '/planes' },
  { key: 'nav.misionVision', path: '/mision-vision' },
  { key: 'nav.contacto', path: '/contacto' },
] satisfies readonly NavLink[];

export default function Navbar(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, toggleColorMode } = useColorMode();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = (): void => setScrolled(window.scrollY > 20);

    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const toggleLang = (): void => {
    void i18n.changeLanguage(i18n.language === 'es' ? 'en' : 'es');
  };

  const isActive = (path: string): boolean => location.pathname === path;

  const navStyles = useMemo(
    () => ({
      appBar: {
        background: scrolled
          ? mode === 'dark'
            ? 'rgba(13,13,20,0.92)'
            : 'rgba(248,246,255,0.92)'
          : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        boxShadow: scrolled ? '0 1px 30px rgba(0,0,0,0.25)' : 'none',
        transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
        borderBottom: scrolled
          ? `1px solid ${mode === 'dark' ? COLORS.dark[400] : COLORS.light[400]}`
          : '1px solid transparent',
      } satisfies SxProps<Theme>,
      link: (active: boolean): SxProps<Theme> => ({
        color: active ? COLORS.purple[400] : theme.palette.text.secondary,
        fontSize: '0.875rem',
        fontWeight: active ? 600 : 400,
        px: 1.5,
        py: 0.5,
        borderRadius: '6px',
        position: 'relative',
        transition: 'color 0.2s ease',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -2,
          left: '50%',
          transform: active ? 'translateX(-50%) scaleX(1)' : 'translateX(-50%) scaleX(0)',
          width: '80%',
          height: '2px',
          background: COLORS.gradients.brand,
          borderRadius: '2px',
          transition: 'transform 0.3s ease',
        },
        '&:hover': {
          color: COLORS.purple[400],
          background: 'transparent',
          '&::after': { transform: 'translateX(-50%) scaleX(1)' },
        },
      }),
    }),
    [mode, scrolled, theme.palette.text.secondary],
  );

  return (
    <>
      <AppBar position="fixed" elevation={0} sx={navStyles.appBar}>
        <Toolbar sx={{ maxWidth: 1280, mx: 'auto', width: '100%', px: { xs: 2, md: 4 }, minHeight: 64 }}>
          {/* Logo */}
          {/* Logo */}
          <Box
            onClick={() => navigate('/')}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              mr: { xs: 2, md: 4 },
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={mode === 'dark' ? '/assets/logo-blanco.svg' : '/assets/logo.svg'}
              alt="LexNova"
              sx={{
                height: { xs: 30, md: 34 },
                width: 'auto',
                display: 'block',
                objectFit: 'contain',
              }}
            />
          </Box>

          {/* Desktop nav */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5, flexGrow: 1 }}>
              {NAV_LINKS.map(({ key, path }) => (
                <Button key={key} onClick={() => navigate(path)} disableRipple sx={navStyles.link(isActive(path))}>
                  {t(key)}
                </Button>
              ))}
            </Box>
          )}

          {/* Right actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            {/* Language toggle */}
            <Tooltip title={i18n.language === 'es' ? 'Switch to English' : 'Cambiar a Español'}>
              <IconButton onClick={toggleLang} size="small" sx={{ color: theme.palette.text.secondary }}>
                <LanguageIcon fontSize="small" />
                <Box component="span" sx={{ fontSize: '0.7rem', ml: 0.5, fontWeight: 700 }}>
                  {i18n.language.toUpperCase()}
                </Box>
              </IconButton>
            </Tooltip>

            {/* Dark/light toggle */}
            <Tooltip title={mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}>
              <IconButton onClick={toggleColorMode} size="small" sx={{ color: theme.palette.text.secondary }}>
                {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            {/* CTA */}
            {!isMobile && (
              <Button
                variant="contained"
                onClick={() => navigate('/contacto')}
                sx={{
                  ml: 1,
                  background: COLORS.gradients.brand,
                  color: '#fff',
                  px: 3,
                  py: 1,
                  fontSize: '0.875rem',
                  '&:hover': { background: COLORS.gradients.brandLight },
                }}
              >
                {t('nav.empezar')}
              </Button>
            )}

            {/* Mobile hamburger */}
            {isMobile && (
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: theme.palette.text.primary }}>
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: mode === 'dark' ? COLORS.dark[800] : '#fff',
            pt: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 2 }}>
          <IconButton onClick={() => setDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {NAV_LINKS.map(({ key, path }) => (
            <ListItem key={key} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(path);
                  setDrawerOpen(false);
                }}
                selected={isActive(path)}
                sx={{
                  '&.Mui-selected': { background: 'rgba(147,51,234,0.1)', color: COLORS.purple[400] },
                }}
              >
                <ListItemText primary={t(key)} />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem sx={{ mt: 2, px: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                navigate('/contacto');
                setDrawerOpen(false);
              }}
              sx={{ background: COLORS.gradients.brand, color: '#fff' }}
            >
              {t('nav.empezar')}
            </Button>
          </ListItem>
        </List>
      </Drawer>

      {/* Toolbar spacer */}
      <Toolbar sx={{ minHeight: 64 }} />
    </>
  );
}
